import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { SHEET_ZOOM_FIT, sheetPagePixels, sheetScaleAt, t, type PrintSheetHtml, type PrintSheetLayout, type SheetFit } from '@bms2/shared';
import { SheetZoomControl } from '../../components/files/SheetZoomControl';
import styles from './DocumentSheetFrame.module.css';

/**
 * 🔒 The sheet on screen is the printer's own HTML (T169). The product owner,
 * 2026-09-22: *"PDF View does not match the actual pdf."* and, asked how it
 * should behave, *"i dont want a wait like preview PDF has. I want instant"*.
 *
 * 💰 There were two implementations of one sheet — the printer's HTML renderer
 * and a second React drawing of the same projection — and every difference the
 * owner could see came from there being two. There is one now, and the screen
 * frames what the printer prints.
 *
 * 🔒 It is instant because the HTML arrives in the page's own answer. Nothing is
 * fetched here, nothing is rendered to PDF, and there is no spinner: the sheet is
 * on screen in the same paint as the page around it.
 *
 * 🔨 **An iframe with `srcdoc`, not a shadow root.** The print stylesheet carries
 * its reset and its typeface on `html, body` and `body` selectors, and a shadow
 * root has no `html` and no `body` for those to match — the sheet would draw in
 * the shell's own font, which is the exact defect this replaces. The frame also
 * isolates absolutely rather than by convention, so no rule of the sheet's can
 * reach the page and no rule of the page's can reach the sheet.
 *
 * 🔨 The frame carries `sandbox` with nothing allowed. The HTML is ours and holds
 * no script, and a document's data is inside it; an inert frame cannot be made to
 * run anything by a value that came from a record.
 *
 * 🔒 The scale is decision 0611's: the pane keeps its own size, the frame stays
 * the page's own pixels, and a `scale()` over it is what grows the paper. Sizing
 * the frame's box instead crops the sheet, because the HTML inside draws its page
 * at fixed physical inches.
 *
 * 🔒 The template editor's own zoom control stands above the pane (T188;
 * decision 0659, amending 0627). The product owner: *"zoom in and out control
 * that is present in the template editor needs to be present in the pdf view of
 * accounting documents"*. 🔨 The sheet still opens at fit page every time and
 * nothing is remembered, so the view stays as instant as decision 0627 made it:
 * a step scales the paper already on screen and asks for nothing.
 *
 * 🔒 The sheet opens fitted to the pane's WIDTH (decision 1452, amending 0611 and
 * 0659 for the document view): the paper takes the pane from edge to edge and the
 * pane scrolls it down. Fit page is one press away and stays live at the first
 * step. 💰 Fit page drew a Sales Order about 340 pixels wide in a pane twice that,
 * its text unreadable without zooming (UI audit of 2026-09-30, rank 9).
 *
 * 🔒 The pane is the one scroller on the PDF view (T205; decision 0694). It takes
 * the height the page leaves beneath the zoom row rather than a height of its own
 * guessed from the window, so the page around it never scrolls beside it. Fit page
 * is still measured against the pane's own box, which the observer below re-reads
 * whenever the window, and so the pane, changes size.
 */
export function DocumentSheetFrame({ sheet, layout, title, corner, controls, lead = null, compact = false, flow = false }: {
  sheet: PrintSheetHtml;
  /**
   * The page's own size in inches; without one the sheet draws at US Letter, which every seeded layout is. A
   * document hands its layout, and a report its sheet's page alone, which is all the frame reads (T350).
   */
  layout?: { page: Pick<PrintSheetLayout['page'], 'widthInches' | 'heightInches'> } | null;
  /** The frame's accessible name, which is the document it draws. */
  title: string;
  /**
   * What the screen lays over the sheet's top-left corner, the status band (decisions 0612, 1457). It is drawn
   * in the sheet's own units and scaled with it, clipped to the paper, and never inside the printer's HTML.
   */
  corner?: ReactNode;
  /**
   * 🔒 The page's own view controls — the download chevron and the Details | PDF
   * pill — drawn at the right end of the zoom row, so the PDF view carries one
   * control row above the sheet and not two (T335; decisions 0626 and
   * 0659, amended in batch N2). On a narrow window the row wraps and the controls take the next line.
   */
  controls?: ReactNode;
  /**
   * 🔒 What stands at the head of the row, before the zoom: on a phone, the Details | PDF switch (T2190; decision
   * 3051). Nothing given, the row opens on the zoom as it always has.
   */
  lead?: ReactNode;
  /**
   * 🔒 The quiet row (decisions 3051, 6703): the switch, the zoom as − and +, Fit Page, the page count and the
   * download at the right. 🔒 Where the frame cannot hold them on one line, the zoom, the count and the download
   * take the next, so Fit Page never stands over the download (decision 11097).
   */
  compact?: boolean;
  /**
   * 🔒 On a phone the page is the one scroller and the pane draws the sheet at its own height (T2189; decision
   * 3050), so what follows the sheet stands after it in the page's flow. The pane scrolls only sideways, and Fit
   * page is measured against the room the page's scroller leaves beneath the zoom row.
   */
  flow?: boolean;
}) {
  const paneRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [pane, setPane] = useState({ widthPx: 0, heightPx: 0 });
  /** 🔒 Fit width on every opening (decision 1452); the fit and the step live as long as the view does. */
  const [zoom, setZoom] = useState<number>(SHEET_ZOOM_FIT);
  const [fit, setFit] = useState<SheetFit>('width');

  /*
   * 🔨 Measured in a layout effect (T359): the box is read and the scale set before
   * the sheet is first shown, so no frame is ever drawn at the unmeasured scale of 1.
   * 💰 A passive effect left that frame on screen for a moment, and a test reading the
   * sheet as soon as it appeared read 1 for 0.53 once on the runner.
   */
  useLayoutEffect(() => {
    const box = paneRef.current;
    if (!box) return;
    /*
     * 🔨 The pane's CONTENT box, less the padding it draws the sheet inside, the
     * way the template editor's preview measures its own (decision 0659). Fit
     * page measured against the padded box made the sheet a little taller than
     * the room there was for it, so its foot sat under the pane's own edge at the
     * one step meant to show the whole page.
     */
    const styled = (element: Element) => (typeof getComputedStyle === 'undefined' ? null : getComputedStyle(element));
    const edge = (value: string | undefined) => Number.parseFloat(value ?? '') || 0;
    /* 🔨 In the page's flow the pane's height follows the sheet, so the page's own scroller is the box Fit page is measured against (decision 3050). */
    const region = flow ? scrollerAbove(box) : null;
    const measure = () => {
      const drawn = styled(box);
      const padding = edge(drawn?.paddingTop) + edge(drawn?.paddingBottom);
      const room = region === null ? box.clientHeight : roomBeneath(region, toolbarRef.current, styled, edge);
      const next = {
        widthPx: Math.max(0, box.clientWidth - edge(drawn?.paddingLeft) - edge(drawn?.paddingRight)),
        heightPx: Math.max(0, room - padding),
      };
      /* 🔨 The pane re-measures as its own height follows a zoom step; an unchanged box keeps the same state, so a step never loops. */
      setPane((held) => (held.widthPx === next.widthPx && held.heightPx === next.heightPx ? held : next));
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(box);
    if (region !== null) observer?.observe(region);
    return () => observer?.disconnect();
  }, [flow]);

  const page = { widthInches: layout?.page.widthInches ?? LETTER.widthInches, heightInches: layout?.page.heightInches ?? LETTER.heightInches };
  /* 🔨 The frame holds every sheet of the document stacked, so its own height is the page's times the count the renderer reported. */
  const pages = Math.max(1, sheet.pageCount);
  const scale = sheetScaleAt(page, pane, fit, zoom);
  const own = sheetPagePixels(page, 1);
  const drawn = sheetPagePixels(page, scale);

  return (
    <div className={styles.frameWrap} {...(flow ? { 'data-document-sheet-flow': 'true' } : {})}>
      {/*
        🔒 The zoom row, with the page count beside it rather than inside the pane, so the pane holds the paper alone and fit page is the whole page (decision 0659).
        🔒 The page's view controls stand at its right end, one row above the sheet (T335; decisions 0626, 0659).
      */}
      <div ref={toolbarRef} className={styles.toolbar} data-document-sheet-toolbar="true" {...(compact ? { 'data-document-sheet-toolbar-shape': 'row' } : {})}>
        {lead === null ? null : <div className={styles.toolbarLead} data-document-sheet-lead="true">{lead}</div>}
        <div className={styles.toolbarStart}>
          <SheetZoomControl zoom={zoom} onZoom={setZoom} fitPage={fit === 'page'} onFitPage={() => { setFit('page'); setZoom(SHEET_ZOOM_FIT); }} compact={compact} attributes={{ 'data-document-sheet-zoom': zoom, 'data-document-sheet-fit': fit }} />
          <p className={styles.pageCount} data-document-sheet-page-count={pages}>
            {pages === 1 ? t('document.sheet.onePage') : `${pages} ${t('document.sheet.pages')}`}
          </p>
        </div>
        {controls ? <div className={styles.toolbarEnd} data-document-sheet-controls="true">{controls}</div> : null}
      </div>
      <div className={styles.paneWrap}>
        <div ref={paneRef} className={styles.pane} data-document-sheet-pane="true">
          <div
            className={styles.stage}
            data-document-sheet-scale={scale.toFixed(4)}
            data-document-sheet-pages={pages}
            style={{ width: `${drawn.widthPx}px`, height: `${drawn.heightPx * pages}px` }}
          >
            <iframe
              className={styles.frame}
              title={title}
              sandbox=""
              srcDoc={sheet.html}
              data-document-sheet="true"
              style={{ width: `${own.widthPx}px`, height: `${own.heightPx * pages}px`, transform: `scale(${scale})` }}
            />
            {/*
              🔒 The status band's layer (decision 1457): the first page's own box at the paper's own pixels, scaled as the
              paper is, so the band grows and shrinks with the sheet at every step and is clipped to the paper's corner.
            */}
            {corner ? (
              <div className={styles.cornerLayer} data-document-sheet-corner="true" style={{ width: `${own.widthPx}px`, height: `${own.heightPx}px`, transform: `scale(${scale})` }}>
                {corner}
              </div>
            ) : null}
            {/*
              * 🔒 The rule where the printer breaks (T169), drawn at the page height the
              * layout declares. 🔨 It is drawn by the screen and never by the sheet, so
              * it cannot reach the paper: the HTML in the frame is the same bytes the
              * printer is given.
              */}
            {Array.from({ length: pages - 1 }, (_ignored, index) => (
              <span
                key={index}
                className={styles.break}
                aria-hidden="true"
                data-document-sheet-break={index + 1}
                style={{ top: `${drawn.heightPx * (index + 1)}px` }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The nearest ancestor that scrolls up and down, which is the page's content region on a document page; null where none does. */
function scrollerAbove(element: Element): Element | null {
  if (typeof getComputedStyle === 'undefined') return null;
  for (let at = element.parentElement; at !== null; at = at.parentElement) {
    if (/auto|scroll/.test(getComputedStyle(at).overflowY)) return at;
  }
  return null;
}

/** The height a scroller shows beneath the zoom row: its own inner height, less its padding, the row and the gap under it. */
function roomBeneath(
  region: Element,
  toolbar: HTMLElement | null,
  styled: (element: Element) => CSSStyleDeclaration | null,
  edge: (value: string | undefined) => number,
): number {
  const own = styled(region);
  const gap = toolbar?.parentElement ? edge(styled(toolbar.parentElement)?.rowGap) : 0;
  return region.clientHeight - edge(own?.paddingTop) - edge(own?.paddingBottom) - (toolbar?.offsetHeight ?? 0) - gap;
}

/** 🔨 US Letter portrait, which is what every seeded layout is; a document whose answer carries no layout still draws at paper size rather than at nothing. */
const LETTER = { widthInches: 8.5, heightInches: 11 };
